import React, { useState, useEffect, useMemo } from 'react';
import { signInWithGoogle } from '../firebase';
import { useAuth } from '../components/AuthContext';
import { IN_TESTING } from '../config/flags';
import { WindowCloseIcon, SignInIcon } from './Icons';
import { evaluateNextOptimalAssessment } from '../utils/triageEngine';

const ACCENT_BLUE = '#31b1c6';

export default function TriageModal({ isOpen, onClose }) {
    const { user, history, isPremium } = useAuth(); 

    const [step, setStep] = useState(1);
    const [pathway, setPathway] = useState(null);
    const [insight, setInsight] = useState(null);

    const dynamicText = useMemo(() => {
        const hour = new Date().getHours();
        const month = new Date().getMonth(); 

        let timeGreeting = "Hello";
        if (hour < 12) timeGreeting = "Good morning";
        else if (hour < 17) timeGreeting = "Good afternoon";
        else if (hour < 22) timeGreeting = "Good evening";
        else timeGreeting = "Working late?";

        let seasonalNudge = "";
        if (month === 11) seasonalNudge = " As we wrap up the year,";
        else if (month === 0) seasonalNudge = " Kicking off the new year,";
        else if (month >= 5 && month <= 7) seasonalNudge = " As we push through the winter months,";
        else if (month >= 8 && month <= 10) seasonalNudge = " With spring in the air,";
        else seasonalNudge = " Right now,";

        const step1Phrases = [
            "select the area that feels most urgent, and I'll guide you to the right tool.",
            "where should we focus our attention? Pick a category below to begin.",
            "let's check your baseline. Which of these areas is currently on your mind?"
        ];
        
        const step2Phrases = [
            "Let's zero in. Which of these sounds most accurate right now?",
            "Got it. To get you the exact right tool, which of these statements resonates more?",
            "Understood. Let's narrow it down. Which of these best describes your situation?"
        ];

        return {
            timeGreeting,
            seasonalNudge,
            step1Label: step1Phrases[Math.floor(Math.random() * step1Phrases.length)],
            step2Label: step2Phrases[Math.floor(Math.random() * step2Phrases.length)]
        };
    }, [isOpen]); 

    useEffect(() => {
        setStep(1);
        setPathway(null);

        if (user) {
            const evaluation = evaluateNextOptimalAssessment(history, user.displayName);
            if (evaluation) {
                setInsight({
                    greeting: `${dynamicText.timeGreeting}, ${evaluation.greetingName}.`,
                    message: evaluation.message,
                    recommendedPath: evaluation.recommendedPath,
                    recommendedLabel: evaluation.recommendedLabel
                });
            } else {
                setInsight(null);
            }
        } else {
            setInsight(null);
        }
    }, [user, history, isOpen, dynamicText]);

    if (!isOpen) return null;

    const getPathwayLabel = () => {
        if (pathway === 'environment') return "🏢 Workload & Environment";
        if (pathway === 'energy') return "🔋 Energy & Burnout";
        if (pathway === 'relational') return "🤝 Team Dynamics";
        if (pathway === 'identity') return "🧠 Self-Discovery & Personality";
        return "";
    };

    const ACCENT_COLOR = 'var(--triage-accent)'; 

    const styles = {
        botBubble: {
            background: 'var(--bg-card)',
            color: 'var(--text-main)',
            padding: '14px 18px',
            borderRadius: '20px 20px 20px 4px',
            fontSize: '1.05rem',
            lineHeight: 1.5,
            maxWidth: '85%',
            boxShadow: '0 2px 8px var(--card-shadow)',
            border: 'none'
        },
        userBubble: {
            alignSelf: 'flex-end',
            background: 'var(--triage-accent-bg)',
            color: 'var(--text-main)',
            padding: '14px 20px',
            borderRadius: '20px 20px 4px 20px',
            fontSize: '1.05rem',
            fontWeight: '600',
            maxWidth: '85%',
            border: '1px solid var(--border-subtle)',
            boxShadow: '0 4px 12px var(--card-shadow)'
        },
        quickReplyBtn: {
            background: 'transparent',
            border: `1.5px solid ${ACCENT_COLOR}`,
            borderRadius: '24px',
            padding: '14px 20px',
            color: ACCENT_COLOR,
            textAlign: 'left',
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
            transition: 'all 0.2s ease',
            boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
        }
    };

    return (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'var(--bg-main)', zIndex: 9999, display: 'flex', flexDirection: 'column', padding: '20px', boxSizing: 'border-box' }}>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', maxWidth: '600px', margin: '0 auto 20px auto', paddingBottom: '15px', borderBottom: '1px solid var(--border-subtle)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <img 
                        src="/icon.png" 
                        alt="Wellbeing Concierge" 
                        style={{ width: '36px', height: '36px', borderRadius: '50%', border: '1px solid var(--border-subtle)', objectFit: 'cover', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)' }} 
                    />
                    <div>
                        <div style={{ fontSize: '15px', fontWeight: 'bold', color: 'var(--text-main)', letterSpacing: '-0.3px' }}>Wellbeing Concierge</div>
                        <div style={{ fontSize: '12px', color: '#10b981', display: 'flex', alignItems: 'center', gap: '4px', fontWeight: '500' }}>
                            <span style={{ width: '6px', height: '6px', background: '#10b981', borderRadius: '50%', display: 'inline-block', boxShadow: '0 0 4px #10b981' }}></span> Active Now
                        </div>
                    </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {(!isPremium && !IN_TESTING) && (
                        <button 
                            onClick={() => window.location.href = '/?triage=closed&view=pricing'}
                            style={{ background: 'var(--card-bg)', color: 'var(--text-main)', border: '1px solid var(--border-subtle)', borderRadius: '20px', padding: '6px 14px', fontSize: '11px', fontWeight: '700', cursor: 'pointer', boxShadow: '0 2px 6px var(--card-shadow)' }}
                        >
                            Buy Pro License (from $2/mo)
                        </button>
                    )}
                    <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '4px' }} title="Close">
                        <WindowCloseIcon size="18px" />
                    </button>
                </div>
            </div>

            <div style={{ width: '100%', maxWidth: '600px', margin: '0 auto', flexGrow: 1, display: 'flex', flexDirection: 'column', gap: '24px', overflowY: 'auto', paddingBottom: '40px' }}>
                
                <div className="animate-slide-up" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginLeft: '10px', fontWeight: '500' }}>Concierge • Just now</div>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
                        <div style={styles.botBubble}>
                            {insight ? (
                                <>
                                    <strong>{insight.greeting}</strong><br/>
                                    {insight.message}
                                </>
                            ) : (
                                `${dynamicText.timeGreeting}. ${dynamicText.seasonalNudge.trim()} ${dynamicText.step1Label}`
                            )}
                        </div>
                    </div>
                </div>

                {step === 1 && (
                    <div className="animate-fade" style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '10px' }}>
                        
                        {insight && insight.recommendedPath && (
                            <button 
                                onClick={() => window.location.href = `/?tool=${insight.recommendedPath}&from=triage`} 
                                style={{ 
                                    ...styles.quickReplyBtn, 
                                    border: '1.5px solid var(--triage-alert)', 
                                    background: 'var(--triage-alert-bg)', 
                                    color: 'var(--triage-alert)' 
                                }}
                            >
                                <span style={{ fontSize: '14px', fontWeight: 'bold' }}>⚡ {insight.recommendedLabel}</span>
                                <span style={{ fontSize: '12px', opacity: 0.8 }}>Based on your recent history flags.</span>
                            </button>
                        )}

                        <button onClick={() => { setPathway('environment'); setStep(2); }} style={styles.quickReplyBtn}>
                            <span style={{ fontSize: '14px', fontWeight: 'bold' }}>🏢 Workload & Environment</span>
                            <span style={{ fontSize: '12px', opacity: 0.8 }}>My tasks feel overwhelming, or I want to evaluate support.</span>
                        </button>

                        <button onClick={() => { setPathway('energy'); setStep(2); }} style={styles.quickReplyBtn}>
                            <span style={{ fontSize: '14px', fontWeight: 'bold' }}>🔋 Energy & Burnout</span>
                            <span style={{ fontSize: '12px', opacity: 0.8 }}>I am feeling stressed, anxious, or deeply exhausted.</span>
                        </button>

                        <button onClick={() => { setPathway('relational'); setStep(2); }} style={styles.quickReplyBtn}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontSize: '14px', fontWeight: 'bold' }}>🤝 Team Dynamics</span>
                                {!isPremium && <span className="pro-badge" style={{ marginTop: '0' }}>PRO</span>}
                            </div>
                            <span style={{ fontSize: '12px', opacity: 0.8 }}>I am experiencing communication friction with a colleague.</span>
                        </button>

                        <button onClick={() => { setPathway('identity'); setStep(2); }} style={styles.quickReplyBtn}>
                            <span style={{ fontSize: '14px', fontWeight: 'bold' }}>🧠 Self-Discovery & Personality</span>
                            <span style={{ fontSize: '12px', opacity: 0.8 }}>I want to explore my core traits and psychological footprint.</span>
                        </button>

                        {!user && (
                            <button onClick={async () => { try { await signInWithGoogle(); } catch { onClose(); } }} style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', fontWeight: 'bold', cursor: 'pointer', fontSize: '13px', marginTop: '15px', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', margin: '15px auto 0 auto' }}>
                                <SignInIcon size="14px" />
                                <span>Sign in to unlock history-based routing</span>
                            </button>
                        )}
                    </div>
                )}

                {step === 2 && (
                    <>
                        <div className="animate-slide-up" style={styles.userBubble}>
                            {getPathwayLabel()}
                        </div>

                        <div className="animate-slide-up" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginLeft: '10px', fontWeight: '500' }}>Concierge • Just now</div>
                            <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
                                <div style={styles.botBubble}>
                                    {dynamicText.step2Label}
                                </div>
                            </div>
                        </div>

                        <div className="animate-fade" style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '10px' }}>
                            {pathway === 'environment' && (
                                <>
                                    <button onClick={() => window.location.href = '/?tool=copsoq&from=triage'} style={styles.quickReplyBtn}>
                                        <div style={{ fontSize: '14px', fontWeight: 'bold' }}>"The pace and volume of my work is relentless."</div>
                                        <div style={{ fontSize: '12px', opacity: 0.8 }}>Launch COPSOQ III (Workplace Demands)</div>
                                    </button>
                                    <button onClick={() => window.location.href = '/?tool=uwes17&from=triage'} style={styles.quickReplyBtn}>
                                        <div style={{ fontSize: '14px', fontWeight: 'bold' }}>"I want to see if I'm still engaged with my core mission."</div>
                                        <div style={{ fontSize: '12px', opacity: 0.8 }}>Launch UWES-17 (Work Engagement)</div>
                                    </button>
                                </>
                            )}

                            {pathway === 'energy' && (
                                <>
                                    <button onClick={() => window.location.href = '/?tool=dass21&from=triage'} style={styles.quickReplyBtn}>
                                        <div style={{ fontSize: '14px', fontWeight: 'bold' }}>"I feel acute, day-to-day anxiety and nervous stress."</div>
                                        <div style={{ fontSize: '12px', opacity: 0.8 }}>Launch DASS-21 (Clinical Stress)</div>
                                    </button>
                                    <button onClick={() => window.location.href = '/?tool=obi16&from=triage'} style={styles.quickReplyBtn}>
                                        <div style={{ fontSize: '14px', fontWeight: 'bold' }}>"I'm not panicked, just deeply exhausted and detached."</div>
                                        <div style={{ fontSize: '12px', opacity: 0.8 }}>Launch OBI-16 (Chronic Burnout)</div>
                                    </button>
                                    <button onClick={() => window.location.href = '/?tool=sds6&from=triage'} style={styles.quickReplyBtn}>
                                        <div style={{ fontSize: '14px', fontWeight: 'bold' }}>"I am experiencing spiritual dryness or a loss of vitality."</div>
                                        <div style={{ fontSize: '12px', opacity: 0.8 }}>Launch SDS-6 (Spiritual Vitality)</div>
                                    </button>
                                </>
                            )}

                            {pathway === 'relational' && (
                                <>
                                    <button onClick={() => window.location.href = '/?tool=disc&from=triage'} style={styles.quickReplyBtn}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', fontWeight: 'bold' }}>
                                            "I want to map my communication style."
                                            {!isPremium && <span className="pro-badge" style={{ fontSize: '8px', padding: '2px 4px' }}>PRO AI</span>}
                                        </div>
                                        <div style={{ fontSize: '12px', opacity: 0.8 }}>Launch DISC Profile</div>
                                    </button>
                                    <button onClick={() => window.location.href = '/?tool=archetype&from=triage'} style={styles.quickReplyBtn}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', fontWeight: 'bold' }}>
                                            "I want to understand my core leadership traits."
                                            {!isPremium && <span className="pro-badge" style={{ fontSize: '8px', padding: '2px 4px' }}>PRO AI</span>}
                                        </div>
                                        <div style={{ fontSize: '12px', opacity: 0.8 }}>Launch Personality Archetypes</div>
                                    </button>
                                    <button onClick={() => window.location.href = '/?tool=sapphire&from=triage'} style={styles.quickReplyBtn}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', fontWeight: 'bold' }}>
                                            "I need a highly advanced executive leadership profile."
                                            {!isPremium && <span className="pro-badge" style={{ fontSize: '8px', padding: '2px 4px' }}>PRO AI</span>}
                                        </div>
                                        <div style={{ fontSize: '12px', opacity: 0.8 }}>Launch Psychometric Matrix</div>
                                    </button>
                                </>
                            )}

                            {pathway === 'identity' && (
                                <>
                                    <button onClick={() => window.location.href = '/?tool=sapphire&from=triage'} style={styles.quickReplyBtn}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', fontWeight: 'bold' }}>
                                            "I need a highly advanced executive leadership profile."
                                            {!isPremium && <span className="pro-badge" style={{ fontSize: '8px', padding: '2px 4px' }}>PRO AI</span>}
                                        </div>
                                        <div style={{ fontSize: '12px', opacity: 0.8 }}>Launch Psychometric Matrix</div>
                                    </button>
                                    <button onClick={() => window.location.href = '/?tool=fivefactor&from=triage'} style={styles.quickReplyBtn}>
                                        <div style={{ fontSize: '14px', fontWeight: 'bold' }}>"I want to map my Big Five psychological traits."</div>
                                        <div style={{ fontSize: '12px', opacity: 0.8 }}>Launch Five Factor (OCEAN)</div>
                                    </button>
                                </>
                            )}

                            <button onClick={() => setStep(1)} style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '13px', marginTop: '10px', textAlign: 'center', fontWeight: '500' }}>
                                ← Start over
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}